import { useState, useEffect } from 'react';
import { useLocalization } from '../../contexts/LocalizationContext';
import discountService from '../../services/discountService';
import productService from '../../services/productService';
import brandService from '../../services/brandService';
import Loading from '../../components/Common/Loading';
import Button from '../../components/Common/Button';
import Table from '../../components/Common/Table';
import Modal from '../../components/Common/Modal';
import Input from '../../components/Common/Input';

const DiscountsIndex = () => {
  const { get, isRtl } = useLocalization();
  const [activeTab, setActiveTab] = useState(0);
  const [productDiscounts, setProductDiscounts] = useState([]);
  const [shippingDiscounts, setShippingDiscounts] = useState([]);
  const [promotions, setPromotions] = useState([]);
  const [isLoading, setIsLoading] = useState(true);
  const [createModalType, setCreateModalType] = useState(null); // 'product' | 'shipping' | 'promotion'
  const [editingItem, setEditingItem] = useState(null); // { type, item }
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [createError, setCreateError] = useState(null);
  const [formData, setFormData] = useState({});
  // Product discount: scope and new-price flow
  const [scopeType, setScopeType] = useState('all'); // 'all' | 'brand' | 'product'
  const [productsList, setProductsList] = useState([]);
  const [brandsList, setBrandsList] = useState([]);
  const [productDetails, setProductDetails] = useState(null);
  const [isLoadingProductDetails, setIsLoadingProductDetails] = useState(false);
  const [newPriceAfterDiscount, setNewPriceAfterDiscount] = useState('');
  const [productSearchQuery, setProductSearchQuery] = useState('');
  const [productDropdownOpen, setProductDropdownOpen] = useState(false);

  const toLocalDatetime = (iso) => (iso ? iso.slice(0, 16) : '');

  useEffect(() => {
    loadData();
  }, []);

  useEffect(() => {
    if (editingItem) {
      const { type, item } = editingItem;
      if (type === 'product') {
        const scope = item.productId ? 'product' : item.brandId ? 'brand' : 'all';
        setScopeType(scope);
        setFormData({
          productId: item.productId ?? null,
          variantId: item.variantId ?? null,
          brandId: item.brandId ?? null,
          percentage: item.percentage ?? '',
          fixedAmount: item.fixedAmount ?? '',
          hasFreeShipping: !!item.hasFreeShipping,
          startDate: toLocalDatetime(item.startsAtUtc),
          endDate: toLocalDatetime(item.endsAtUtc),
          isActive: !!item.isActive,
          priority: item.priority ?? 0,
        });
        setNewPriceAfterDiscount('');
        if (item.productId) {
          setProductDetails(null);
          productService.getDetails(item.productId).then((details) => {
            setProductDetails(details);
            const variant = details?.variants?.find((v) => v.id === item.variantId);
            if (variant) {
              const orig = variant.originalPrice ?? variant.price;
              if (item.percentage != null) setNewPriceAfterDiscount(String((orig * (1 - item.percentage / 100)).toFixed(2)));
              else if (item.fixedAmount != null) setNewPriceAfterDiscount(String(Math.max(0, orig - item.fixedAmount)));
            }
          }).catch(() => setProductDetails(null));
        } else {
          setProductDetails(null);
        }
        Promise.all([productService.getAll().catch(() => []), brandService.getAll().catch(() => [])]).then(([products, brands]) => {
          setProductsList(products);
          setBrandsList(brands);
        });
      } else if (type === 'shipping') {
        setFormData({
          titleEn: item.titleEn ?? '',
          titleAr: item.titleAr ?? '',
          descriptionEn: item.descriptionEn ?? '',
          descriptionAr: item.descriptionAr ?? '',
          discountType: item.discountType ?? 0,
          discountValue: item.discountValue ?? '',
          isFreeShipping: !!item.isFreeShipping,
          appliesToAllProducts: !!item.appliesToAllProducts,
          minOrderAmount: item.minOrderAmount ?? '',
          startDate: toLocalDatetime(item.startsAtUtc),
          endDate: toLocalDatetime(item.endsAtUtc),
          isActive: !!item.isActive,
          priority: item.priority ?? 0,
        });
      } else if (type === 'promotion') {
        setFormData({
          messageEn: item.messageEn ?? '',
          messageAr: item.messageAr ?? '',
          buttonTextEn: item.buttonTextEn ?? '',
          buttonTextAr: item.buttonTextAr ?? '',
          redirectUrl: item.redirectUrl ?? '',
          filterType: item.filterType ?? 0,
          filterValue: item.filterValue ?? '',
          startDate: toLocalDatetime(item.startsAtUtc),
          endDate: toLocalDatetime(item.endsAtUtc),
          isActive: !!item.isActive,
          priority: item.priority ?? 0,
          displayOrder: item.displayOrder ?? 0,
        });
      }
      setCreateError(null);
      return;
    }
    if (createModalType === 'product') {
      const now = new Date();
      const nextMonth = new Date(now);
      nextMonth.setMonth(nextMonth.getMonth() + 1);
      setScopeType('all');
      setProductDetails(null);
      setNewPriceAfterDiscount('');
      setProductSearchQuery('');
      setProductDropdownOpen(false);
      setFormData({
        productId: null,
        variantId: null,
        brandId: null,
        percentage: 10,
        fixedAmount: '',
        hasFreeShipping: false,
        startDate: now.toISOString().slice(0, 16),
        endDate: nextMonth.toISOString().slice(0, 16),
        isActive: true,
        priority: 0,
      });
      Promise.all([productService.getAll().catch(() => []), brandService.getAll().catch(() => [])]).then(([products, brands]) => {
        setProductsList(products);
        setBrandsList(brands);
      });
    } else if (createModalType === 'shipping') {
      const now = new Date();
      const nextMonth = new Date(now);
      nextMonth.setMonth(nextMonth.getMonth() + 1);
      setFormData({
        titleEn: '',
        titleAr: '',
        descriptionEn: '',
        descriptionAr: '',
        discountType: 0,
        discountValue: '',
        isFreeShipping: false,
        appliesToAllProducts: true,
        minOrderAmount: '',
        startDate: now.toISOString().slice(0, 16),
        endDate: nextMonth.toISOString().slice(0, 16),
        isActive: true,
        priority: 0,
      });
    } else if (createModalType === 'promotion') {
      const now = new Date();
      const nextMonth = new Date(now);
      nextMonth.setMonth(nextMonth.getMonth() + 1);
      setFormData({
        messageEn: '',
        messageAr: '',
        buttonTextEn: '',
        buttonTextAr: '',
        redirectUrl: '',
        filterType: 0,
        filterValue: '',
        startDate: now.toISOString().slice(0, 16),
        endDate: nextMonth.toISOString().slice(0, 16),
        isActive: true,
        priority: 0,
        displayOrder: 0,
      });
    } else {
      setFormData({});
    }
    setCreateError(null);
  }, [createModalType, editingItem]);

  const updateForm = (key, value) => setFormData((prev) => ({ ...prev, [key]: value }));

  const loadProductDetails = (productId) => {
    if (!productId) {
      setProductDetails(null);
      updateForm('variantId', null);
      setNewPriceAfterDiscount('');
      return;
    }
    setIsLoadingProductDetails(true);
    setProductDetails(null);
    updateForm('variantId', null);
    setNewPriceAfterDiscount('');
    productService
      .getDetails(productId)
      .then(setProductDetails)
      .catch(() => setProductDetails(null))
      .finally(() => setIsLoadingProductDetails(false));
  };

  const selectedVariant = productDetails?.variants?.find((v) => v.id === formData.variantId);
  const originalPrice = selectedVariant ? (selectedVariant.originalPrice ?? selectedVariant.price) : null;
  const newPriceNum = newPriceAfterDiscount !== '' && newPriceAfterDiscount != null ? parseFloat(newPriceAfterDiscount) : null;
  const computedPercentage =
    originalPrice != null && originalPrice > 0 && newPriceNum != null && newPriceNum >= 0 && newPriceNum < originalPrice
      ? Math.round((1 - newPriceNum / originalPrice) * 10000) / 100
      : null;
  const newPriceError =
    selectedVariant && newPriceNum != null
      ? newPriceNum < 0
        ? (isRtl ? 'السعر الجديد يجب أن يكون 0 أو أكثر' : 'New price must be 0 or greater.')
        : newPriceNum >= originalPrice
          ? (isRtl ? 'السعر الجديد يجب أن يكون أقل من السعر الأصلي' : 'New price must be less than original price.')
          : null
      : null;
  const canSubmitProductScope =
    scopeType === 'product' &&
    formData.productId &&
    formData.variantId &&
    selectedVariant &&
    newPriceNum != null &&
    !newPriceError &&
    productDetails?.variants?.length > 0;

  const productDisplayName = (p) => (isRtl ? (p.nameAr || p.nameEn) : (p.nameEn || p.nameAr)) || '';
  const selectedProduct = formData.productId ? productsList.find((p) => p.id === formData.productId) : null;
  const filteredProducts = productSearchQuery.trim()
    ? productsList.filter(
        (p) =>
          (p.nameEn && p.nameEn.toLowerCase().includes(productSearchQuery.trim().toLowerCase())) ||
          (p.nameAr && p.nameAr.toLowerCase().includes(productSearchQuery.trim().toLowerCase()))
      )
    : productsList;

  const handleCloseModal = () => {
    if (!isSubmitting) {
      setCreateModalType(null);
      setEditingItem(null);
      setScopeType('all');
      setProductDetails(null);
      setNewPriceAfterDiscount('');
      setProductSearchQuery('');
      setProductDropdownOpen(false);
    }
  };

  const openEdit = (type, item) => {
    setEditingItem({ type, item });
  };

  const toUtcIso = (localDatetime) =>
    localDatetime ? new Date(localDatetime).toISOString() : null;

  const modalType = editingItem ? editingItem.type : createModalType;

  const handleSaveSubmit = async (e) => {
    e.preventDefault();
    setCreateError(null);
    setIsSubmitting(true);
    try {
      const startsAt = toUtcIso(formData.startDate) || new Date().toISOString();
      const endsAt = toUtcIso(formData.endDate) || null;

      if (editingItem) {
        const { type, item } = editingItem;
        const id = item.id;
        if (type === 'product') {
          const payload = {
            productId: scopeType === 'product' ? formData.productId : null,
            variantId: scopeType === 'product' ? formData.variantId : null,
            brandId: scopeType === 'brand' ? formData.brandId : null,
            percentage: scopeType === 'product' && computedPercentage != null ? computedPercentage : (formData.percentage != null && formData.percentage !== '' ? Number(formData.percentage) : null),
            fixedAmount: scopeType === 'product' ? null : (formData.fixedAmount != null && formData.fixedAmount !== '' ? Number(formData.fixedAmount) : null),
            hasFreeShipping: !!formData.hasFreeShipping,
            startsAtUtc: startsAt,
            endsAtUtc: endsAt,
            isActive: !!formData.isActive,
            priority: Number(formData.priority) || 0,
          };
          await discountService.updateProductDiscount(id, payload);
        } else if (type === 'shipping') {
          await discountService.updateShippingDiscount(id, {
            titleEn: formData.titleEn || '',
            titleAr: formData.titleAr || '',
            descriptionEn: formData.descriptionEn || null,
            descriptionAr: formData.descriptionAr || null,
            discountType: Number(formData.discountType) || 0,
            discountValue: formData.discountValue !== '' ? Number(formData.discountValue) : null,
            isFreeShipping: !!formData.isFreeShipping,
            appliesToAllProducts: !!formData.appliesToAllProducts,
            minOrderAmount: formData.minOrderAmount !== '' ? Number(formData.minOrderAmount) : null,
            startsAtUtc: startsAt,
            endsAtUtc: endsAt,
            isActive: !!formData.isActive,
            priority: Number(formData.priority) || 0,
          });
        } else if (type === 'promotion') {
          await discountService.updatePromotionAnnouncement(id, {
            messageEn: formData.messageEn || '',
            messageAr: formData.messageAr || '',
            buttonTextEn: formData.buttonTextEn || '',
            buttonTextAr: formData.buttonTextAr || '',
            redirectUrl: formData.redirectUrl || null,
            filterType: Number(formData.filterType) || 0,
            filterValue: formData.filterValue || null,
            associatedDiscountId: item.associatedDiscountId ?? null,
            discountAssociationType: item.discountAssociationType ?? null,
            startsAtUtc: startsAt,
            endsAtUtc: endsAt,
            isActive: !!formData.isActive,
            priority: Number(formData.priority) || 0,
            displayOrder: Number(formData.displayOrder) || 0,
          });
        }
      } else if (createModalType === 'product') {
        if (scopeType === 'product' && !canSubmitProductScope) {
          setCreateError(isRtl ? 'اختر المنتج والصنف وأدخل سعراً جديداً صالحاً (أقل من السعر الأصلي).' : 'Please select a product, variant, and enter a valid new price (less than original price).');
          setIsSubmitting(false);
          return;
        }
        await discountService.createProductDiscount({
          productId: scopeType === 'product' ? formData.productId : null,
          variantId: scopeType === 'product' ? formData.variantId : null,
          brandId: scopeType === 'brand' ? formData.brandId : null,
          percentage: scopeType === 'product' && computedPercentage != null ? computedPercentage : (formData.percentage != null && formData.percentage !== '' ? Number(formData.percentage) : null),
          fixedAmount: scopeType === 'product' ? null : (formData.fixedAmount != null && formData.fixedAmount !== '' ? Number(formData.fixedAmount) : null),
          hasFreeShipping: !!formData.hasFreeShipping,
          startsAtUtc: startsAt,
          endsAtUtc: endsAt,
          isActive: !!formData.isActive,
          priority: Number(formData.priority) || 0,
        });
      } else if (createModalType === 'shipping') {
        await discountService.createShippingDiscount({
          titleEn: formData.titleEn || '',
          titleAr: formData.titleAr || '',
          descriptionEn: formData.descriptionEn || null,
          descriptionAr: formData.descriptionAr || null,
          discountType: Number(formData.discountType) || 0,
          discountValue: formData.discountValue !== '' ? Number(formData.discountValue) : null,
          isFreeShipping: !!formData.isFreeShipping,
          appliesToAllProducts: !!formData.appliesToAllProducts,
          minOrderAmount: formData.minOrderAmount !== '' ? Number(formData.minOrderAmount) : null,
          startsAtUtc: startsAt,
          endsAtUtc: endsAt,
          isActive: !!formData.isActive,
          priority: Number(formData.priority) || 0,
        });
      } else if (createModalType === 'promotion') {
        await discountService.createPromotionAnnouncement({
          messageEn: formData.messageEn || '',
          messageAr: formData.messageAr || '',
          buttonTextEn: formData.buttonTextEn || '',
          buttonTextAr: formData.buttonTextAr || '',
          redirectUrl: formData.redirectUrl || null,
          filterType: Number(formData.filterType) || 0,
          filterValue: formData.filterValue || null,
          associatedDiscountId: null,
          discountAssociationType: null,
          startsAtUtc: startsAt,
          endsAtUtc: endsAt,
          isActive: !!formData.isActive,
          priority: Number(formData.priority) || 0,
          displayOrder: Number(formData.displayOrder) || 0,
        });
      }
      await loadData();
      handleCloseModal();
    } catch (err) {
      setCreateError(err?.response?.data?.message || err?.message || (isRtl ? 'حدث خطأ' : 'Something went wrong'));
    } finally {
      setIsSubmitting(false);
    }
  };

  const handleDelete = async (type, item) => {
    const msg = isRtl ? 'هل أنت متأكد من الحذف؟' : 'Are you sure you want to delete this?';
    if (!window.confirm(msg)) return;
    try {
      const id = item.id;
      if (type === 'product') await discountService.deleteProductDiscount(id);
      else if (type === 'shipping') await discountService.deleteShippingDiscount(id);
      else if (type === 'promotion') await discountService.deletePromotionAnnouncement(id);
      await loadData();
    } catch (err) {
      alert(err?.response?.data?.message || err?.message || (isRtl ? 'فشل الحذف' : 'Delete failed'));
    }
  };

  const renderActions = (type, row) => (
    <div className="table-actions" onClick={(e) => e.stopPropagation()}>
      <button
        type="button"
        className="btn-action btn-edit"
        onClick={() => openEdit(type, row)}
        title={isRtl ? 'تعديل' : 'Edit'}
      >
        <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2"><path d="M11 4H4a2 2 0 0 0-2 2v14a2 2 0 0 0 2 2h14a2 2 0 0 0 2-2v-7" /><path d="M18.5 2.5a2.121 2.121 0 0 1 3 3L12 15l-4 1 1-4 9.5-9.5z" /></svg>
        {isRtl ? 'تعديل' : 'Edit'}
      </button>
      <button
        type="button"
        className="btn-action btn-delete"
        onClick={() => handleDelete(type, row)}
        title={isRtl ? 'حذف' : 'Delete'}
      >
        <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2"><path d="M3 6h18M19 6v14a2 2 0 0 1-2 2H7a2 2 0 0 1-2-2V6m3 0V4a2 2 0 0 1 2-2h4a2 2 0 0 1 2 2v2" /><line x1="10" y1="11" x2="10" y2="17" /><line x1="14" y1="11" x2="14" y2="17" /></svg>
        {isRtl ? 'حذف' : 'Delete'}
      </button>
    </div>
  );

  const loadData = async () => {
    try {
      setIsLoading(true);
      const [productData, shippingData, promotionData] = await Promise.all([
        discountService.getProductDiscounts().catch(() => []),
        discountService.getShippingDiscounts().catch(() => []),
        discountService.getPromotionAnnouncements().catch(() => []),
      ]);
      setProductDiscounts(productData);
      setShippingDiscounts(shippingData);
      setPromotions(promotionData);
    } catch (error) {
      console.error('Error loading discounts:', error);
    } finally {
      setIsLoading(false);
    }
  };

  if (isLoading) {
    return <Loading text="Loading discounts..." />;
  }

  return (
    <div className="discounts-content" dir={isRtl ? 'rtl' : 'ltr'}>
      <div className="page-header">
        <div className="page-header-content">
          <div className="page-header-main">
            <h1 className="page-title">{get('discounts_promotions')}</h1>
            <p className="page-description">{get('manage_discounts_promotions')}</p>
          </div>
        </div>
      </div>

      <div className="tabs-container">
        <div className="tabs-header">
          <button
            className={`tab-button ${activeTab === 0 ? 'active' : ''}`}
            onClick={() => setActiveTab(0)}
          >
            {get('product_discounts')}
          </button>
          <button
            className={`tab-button ${activeTab === 1 ? 'active' : ''}`}
            onClick={() => setActiveTab(1)}
          >
            {get('shipping_discounts')}
          </button>
          <button
            className={`tab-button ${activeTab === 2 ? 'active' : ''}`}
            onClick={() => setActiveTab(2)}
          >
            {get('promotion_banners')}
          </button>
        </div>

        <div className="tab-content">
          {activeTab === 0 && (
            <div className="content-section">
              <div className="section-header">
                <h2>{get('product_discounts')}</h2>
                <Button variant="primary" onClick={() => setCreateModalType('product')}>
                  <svg width="16" height="16" viewBox="0 0 24 24" fill="currentColor">
                    <path d="M19 13h-6v6h-2v-6H5v-2h6V5h2v6h6v2z" />
                  </svg>
                  {isRtl ? 'إضافة خصم منتج' : 'Add Product Discount'}
                </Button>
              </div>
              <Table
                columns={[
                  { header: isRtl ? 'الاسم' : 'Name', accessor: 'productNameEn', render: (value, row) => (isRtl ? (row.productNameAr || row.productNameEn) : (row.productNameEn || row.productNameAr)) || '—' },
                  { header: isRtl ? 'النسبة' : 'Percentage', accessor: 'percentage', render: (value) => value != null ? `${value}%` : '—' },
                  { header: isRtl ? 'تاريخ البداية' : 'Start Date', accessor: 'startsAtUtc', render: (value) => value ? new Date(value).toLocaleDateString() : '—' },
                  { header: isRtl ? 'تاريخ النهاية' : 'End Date', accessor: 'endsAtUtc', render: (value) => value ? new Date(value).toLocaleDateString() : '—' },
                  { header: isRtl ? 'إجراءات' : 'Actions', accessor: 'id', render: (_, row) => renderActions('product', row) },
                ]}
                data={productDiscounts}
                emptyMessage={isRtl ? 'لا توجد خصومات منتجات' : 'No product discounts found'}
              />
            </div>
          )}

          {activeTab === 1 && (
            <div className="content-section">
              <div className="section-header">
                <h2>{get('shipping_discounts')}</h2>
                <Button variant="primary" onClick={() => setCreateModalType('shipping')}>
                  <svg width="16" height="16" viewBox="0 0 24 24" fill="currentColor">
                    <path d="M19 13h-6v6h-2v-6H5v-2h6V5h2v6h6v2z" />
                  </svg>
                  {isRtl ? 'إضافة خصم شحن' : 'Add Shipping Discount'}
                </Button>
              </div>
              <Table
                columns={[
                  { header: isRtl ? 'الاسم' : 'Name', accessor: 'titleEn', render: (value, row) => (isRtl ? (row.titleAr || row.titleEn) : (row.titleEn || row.titleAr)) || '—' },
                  { header: isRtl ? 'الحد الأدنى' : 'Minimum Amount', accessor: 'minOrderAmount', render: (value) => value != null ? `${value} IQD` : '—' },
                  { header: isRtl ? 'الخصم' : 'Discount', accessor: 'discountValue', render: (value, row) => row.isFreeShipping ? (isRtl ? 'شحن مجاني' : 'Free shipping') : (value != null ? `${value} IQD` : '—') },
                  { header: isRtl ? 'إجراءات' : 'Actions', accessor: 'id', render: (_, row) => renderActions('shipping', row) },
                ]}
                data={shippingDiscounts}
                emptyMessage={isRtl ? 'لا توجد خصومات شحن' : 'No shipping discounts found'}
              />
            </div>
          )}

          {activeTab === 2 && (
            <div className="content-section">
              <div className="section-header">
                <h2>{get('promotion_banners')}</h2>
                <Button variant="primary" onClick={() => setCreateModalType('promotion')}>
                  <svg width="16" height="16" viewBox="0 0 24 24" fill="currentColor">
                    <path d="M19 13h-6v6h-2v-6H5v-2h6V5h2v6h6v2z" />
                  </svg>
                  {isRtl ? 'إضافة لافتة ترويجية' : 'Add Promotion Banner'}
                </Button>
              </div>
              <Table
                columns={[
                  { header: isRtl ? 'العنوان' : 'Title', accessor: 'messageEn', render: (value, row) => (isRtl ? (row.messageAr || row.messageEn) : (row.messageEn || row.messageAr)) || '—' },
                  { header: isRtl ? 'النص' : 'Text', accessor: 'messageEn', render: (value, row) => (isRtl ? row.messageAr : row.messageEn) || '—' },
                  { header: isRtl ? 'الحالة' : 'Status', accessor: 'isActive', render: (value) => (
                    <span className={`badge ${value ? 'badge-success' : 'badge-neutral'}`}>
                      {value ? (isRtl ? 'نشط' : 'Active') : (isRtl ? 'غير نشط' : 'Inactive')}
                    </span>
                  )},
                  { header: isRtl ? 'إجراءات' : 'Actions', accessor: 'id', render: (_, row) => renderActions('promotion', row) },
                ]}
                data={promotions}
                emptyMessage={isRtl ? 'لا توجد لافتات ترويجية' : 'No promotion banners found'}
              />
            </div>
          )}
        </div>
      </div>

      {/* Create / Edit modal */}
      <Modal
        isOpen={!!createModalType || !!editingItem}
        onClose={handleCloseModal}
        title={
          editingItem
            ? (editingItem.type === 'product'
                ? (isRtl ? 'تعديل خصم منتج' : 'Edit Product Discount')
                : editingItem.type === 'shipping'
                  ? (isRtl ? 'تعديل خصم شحن' : 'Edit Shipping Discount')
                  : (isRtl ? 'تعديل لافتة ترويجية' : 'Edit Promotion Banner'))
            : createModalType === 'product'
              ? (isRtl ? 'إضافة خصم منتج' : 'Add Product Discount')
              : createModalType === 'shipping'
                ? (isRtl ? 'إضافة خصم شحن' : 'Add Shipping Discount')
                : createModalType === 'promotion'
                  ? (isRtl ? 'إضافة لافتة ترويجية' : 'Add Promotion Banner')
                  : ''
        }
        size="default"
        footer={
          <div className="modal-actions">
            <Button variant="secondary" onClick={handleCloseModal} disabled={isSubmitting}>
              {isRtl ? 'إلغاء' : 'Cancel'}
            </Button>
            <Button
              variant="primary"
              onClick={handleSaveSubmit}
              disabled={isSubmitting || (modalType === 'product' && scopeType === 'product' && !canSubmitProductScope)}
            >
              {isSubmitting ? (isRtl ? 'جاري الحفظ...' : 'Saving...') : (isRtl ? 'حفظ' : 'Save')}
            </Button>
          </div>
        }
      >
        <form onSubmit={handleSaveSubmit} className="discount-form">
          {createError && (
            <div className="form-error" style={{ marginBottom: 'var(--sp-4)', padding: 'var(--sp-3)', background: 'var(--danger)', color: 'white', borderRadius: 'var(--r-md)' }}>
              {createError}
            </div>
          )}

          {modalType === 'product' && (
            <div className="form-fields">
              <div className="form-group">
                <label className="form-label">{isRtl ? 'ينطبق على' : 'Apply to'}</label>
                <select
                  className="form-input"
                  value={scopeType}
                  onChange={(e) => {
                    const v = e.target.value;
                    setScopeType(v);
                    if (v !== 'product') {
                      setProductDetails(null);
                      setNewPriceAfterDiscount('');
                      setProductSearchQuery('');
                      setProductDropdownOpen(false);
                      updateForm('productId', null);
                      updateForm('variantId', null);
                    }
                    if (v !== 'brand') updateForm('brandId', null);
                  }}
                >
                  <option value="all">{isRtl ? 'كل المنتجات' : 'All products'}</option>
                  <option value="brand">{isRtl ? 'علامة تجارية محددة' : 'Specific brand'}</option>
                  <option value="product">{isRtl ? 'منتج محدد' : 'Specific product'}</option>
                </select>
              </div>

              {scopeType === 'brand' && (
                <div className="form-group">
                  <label className="form-label">{isRtl ? 'العلامة التجارية' : 'Brand'}</label>
                  <select className="form-input" value={formData.brandId ?? ''} onChange={(e) => updateForm('brandId', e.target.value || null)}>
                    <option value="">{isRtl ? 'اختر العلامة التجارية' : 'Select brand'}</option>
                    {brandsList.map((b) => (
                      <option key={b.id} value={b.id}>{isRtl ? (b.nameAr || b.nameEn) : (b.nameEn || b.nameAr)}</option>
                    ))}
                  </select>
                </div>
              )}

              {scopeType === 'product' && (
                <>
                  <div className="form-group product-typeahead-wrap">
                    <label className="form-label">{isRtl ? 'المنتج' : 'Product'}</label>
                    <div className="product-typeahead">
                      {selectedProduct ? (
                        <div className="product-typeahead-selected">
                          <span className="product-typeahead-selected-name">{productDisplayName(selectedProduct)}</span>
                          <button
                            type="button"
                            className="product-typeahead-clear"
                            onClick={() => {
                              updateForm('productId', null);
                              loadProductDetails(null);
                              setProductSearchQuery('');
                              setProductDropdownOpen(true);
                            }}
                            title={isRtl ? 'تغيير' : 'Change'}
                          >
                            {isRtl ? 'تغيير' : 'Change'}
                          </button>
                        </div>
                      ) : (
                        <>
                          <input
                            type="text"
                            className="form-input product-typeahead-input"
                            placeholder={isRtl ? 'اختر المنتج أو اكتب للبحث...' : 'Select product or type to search...'}
                            value={productSearchQuery}
                            onChange={(e) => {
                              setProductSearchQuery(e.target.value);
                              setProductDropdownOpen(true);
                            }}
                            onFocus={() => setProductDropdownOpen(true)}
                            onBlur={() => setTimeout(() => setProductDropdownOpen(false), 200)}
                            autoComplete="off"
                          />
                          {productDropdownOpen && (
                            <div className="product-typeahead-dropdown">
                              {filteredProducts.length === 0 ? (
                                <div className="product-typeahead-empty">{isRtl ? 'لا توجد نتائج' : 'No results'}</div>
                              ) : (
                                filteredProducts.map((p) => (
                                  <button
                                    key={p.id}
                                    type="button"
                                    className="product-typeahead-option"
                                    onMouseDown={(e) => {
                                      e.preventDefault();
                                      updateForm('productId', p.id);
                                      loadProductDetails(p.id);
                                      setProductSearchQuery('');
                                      setProductDropdownOpen(false);
                                    }}
                                  >
                                    {productDisplayName(p)}
                                  </button>
                                ))
                              )}
                            </div>
                          )}
                        </>
                      )}
                    </div>
                  </div>
                  {isLoadingProductDetails && (
                    <div className="form-hint">{isRtl ? 'جاري تحميل تفاصيل المنتج...' : 'Loading product details...'}</div>
                  )}
                  {!isLoadingProductDetails && productDetails?.variants?.length === 0 && formData.productId && (
                    <div className="form-hint form-hint-error">{isRtl ? 'هذا المنتج ليس له أصناف. أضف أصنافاً أولاً.' : 'This product has no variants. Add variants first.'}</div>
                  )}
                  {!isLoadingProductDetails && productDetails?.variants?.length > 0 && (
                    <>
                      <div className="form-group">
                        <label className="form-label">{isRtl ? 'الصنف' : 'Variant'}</label>
                        <select
                          className="form-input"
                          value={formData.variantId ?? ''}
                          onChange={(e) => {
                            updateForm('variantId', e.target.value || null);
                            setNewPriceAfterDiscount('');
                          }}
                        >
                          <option value="">{isRtl ? 'اختر الصنف' : 'Select variant'}</option>
                          {productDetails.variants.map((v) => (
                            <option key={v.id} value={v.id}>{v.sizeLabelEn || v.sizeLabelAr} – {(v.originalPrice ?? v.price).toLocaleString()}</option>
                          ))}
                        </select>
                      </div>
                      {selectedVariant && (
                        <>
                          <div className="form-group">
                            <label className="form-label">{isRtl ? 'السعر الأصلي' : 'Original price'}</label>
                            <div className="form-readonly">{(originalPrice ?? 0).toLocaleString()} {productDetails.currencyCode || ''}</div>
                          </div>
                          <Input
                            label={isRtl ? 'السعر الجديد بعد الخصم' : 'New price after discount'}
                            type="number"
                            value={newPriceAfterDiscount}
                            onChange={(e) => setNewPriceAfterDiscount(e.target.value)}
                            min={0}
                            step={0.01}
                          />
                          {newPriceError && <div className="form-hint form-hint-error">{newPriceError}</div>}
                          <div className="form-group">
                            <label className="form-label">{isRtl ? 'الخصم' : 'Discount'}</label>
                            <div className="form-readonly">{computedPercentage != null ? `${computedPercentage}%` : '—'}</div>
                          </div>
                        </>
                      )}
                    </>
                  )}
                </>
              )}

              {scopeType !== 'product' && (
                <>
                  <Input label={isRtl ? 'النسبة %' : 'Percentage %'} type="number" value={formData.percentage ?? ''} onChange={(e) => updateForm('percentage', e.target.value)} min={0} max={100} step={0.01} />
                  <Input label={isRtl ? 'مبلغ ثابت (اختياري)' : 'Fixed amount (optional)'} type="number" value={formData.fixedAmount ?? ''} onChange={(e) => updateForm('fixedAmount', e.target.value)} min={0} />
                </>
              )}

              <div className="form-group">
                <label className="form-label">
                  <input type="checkbox" checked={!!formData.hasFreeShipping} onChange={(e) => updateForm('hasFreeShipping', e.target.checked)} />
                  {isRtl ? 'شحن مجاني' : 'Free shipping'}
                </label>
              </div>
              <Input label={isRtl ? 'تاريخ البداية *' : 'Start date *'} type="datetime-local" value={formData.startDate ?? ''} onChange={(e) => updateForm('startDate', e.target.value)} required />
              <Input label={isRtl ? 'تاريخ النهاية (اختياري)' : 'End date (optional)'} type="datetime-local" value={formData.endDate ?? ''} onChange={(e) => updateForm('endDate', e.target.value)} />
              <div className="form-group">
                <label className="form-label">
                  <input type="checkbox" checked={!!formData.isActive} onChange={(e) => updateForm('isActive', e.target.checked)} />
                  {isRtl ? 'نشط' : 'Active'}
                </label>
              </div>
              <Input label={isRtl ? 'الأولوية' : 'Priority'} type="number" value={formData.priority ?? 0} onChange={(e) => updateForm('priority', e.target.value)} min={0} />
            </div>
          )}

          {modalType === 'shipping' && (
            <div className="form-fields">
              <Input label={isRtl ? 'العنوان (EN)' : 'Title (EN)'} value={formData.titleEn ?? ''} onChange={(e) => updateForm('titleEn', e.target.value)} required />
              <Input label={isRtl ? 'العنوان (AR)' : 'Title (AR)'} value={formData.titleAr ?? ''} onChange={(e) => updateForm('titleAr', e.target.value)} required />
              <Input label={isRtl ? 'الوصف (EN اختياري)' : 'Description (EN optional)'} value={formData.descriptionEn ?? ''} onChange={(e) => updateForm('descriptionEn', e.target.value)} />
              <Input label={isRtl ? 'الوصف (AR اختياري)' : 'Description (AR optional)'} value={formData.descriptionAr ?? ''} onChange={(e) => updateForm('descriptionAr', e.target.value)} />
              <div className="form-group">
                <label className="form-label">{isRtl ? 'نوع الخصم' : 'Discount type'}</label>
                <select className="form-input" value={formData.discountType ?? 0} onChange={(e) => updateForm('discountType', e.target.value)}>
                  <option value={0}>{isRtl ? 'شحن مجاني' : 'Free shipping'}</option>
                  <option value={1}>{isRtl ? 'نسبة مئوية' : 'Percentage'}</option>
                  <option value={2}>{isRtl ? 'مبلغ ثابت' : 'Fixed amount'}</option>
                </select>
              </div>
              <Input label={isRtl ? 'قيمة الخصم (اختياري)' : 'Discount value (optional)'} type="number" value={formData.discountValue ?? ''} onChange={(e) => updateForm('discountValue', e.target.value)} min={0} />
              <div className="form-group">
                <label className="form-label">
                  <input type="checkbox" checked={!!formData.isFreeShipping} onChange={(e) => updateForm('isFreeShipping', e.target.checked)} />
                  {isRtl ? 'شحن مجاني' : 'Free shipping'}
                </label>
              </div>
              <div className="form-group">
                <label className="form-label">
                  <input type="checkbox" checked={!!formData.appliesToAllProducts} onChange={(e) => updateForm('appliesToAllProducts', e.target.checked)} />
                  {isRtl ? 'ينطبق على كل المنتجات' : 'Applies to all products'}
                </label>
              </div>
              <Input label={isRtl ? 'الحد الأدنى للطلب (اختياري)' : 'Min order amount (optional)'} type="number" value={formData.minOrderAmount ?? ''} onChange={(e) => updateForm('minOrderAmount', e.target.value)} min={0} />
              <Input label={isRtl ? 'تاريخ البداية' : 'Start date'} type="datetime-local" value={formData.startDate ?? ''} onChange={(e) => updateForm('startDate', e.target.value)} required />
              <Input label={isRtl ? 'تاريخ النهاية (اختياري)' : 'End date (optional)'} type="datetime-local" value={formData.endDate ?? ''} onChange={(e) => updateForm('endDate', e.target.value)} />
              <div className="form-group">
                <label className="form-label">
                  <input type="checkbox" checked={!!formData.isActive} onChange={(e) => updateForm('isActive', e.target.checked)} />
                  {isRtl ? 'نشط' : 'Active'}
                </label>
              </div>
              <Input label={isRtl ? 'الأولوية' : 'Priority'} type="number" value={formData.priority ?? 0} onChange={(e) => updateForm('priority', e.target.value)} min={0} />
            </div>
          )}

          {modalType === 'promotion' && (
            <div className="form-fields">
              <Input label={isRtl ? 'الرسالة (EN)' : 'Message (EN)'} value={formData.messageEn ?? ''} onChange={(e) => updateForm('messageEn', e.target.value)} required />
              <Input label={isRtl ? 'الرسالة (AR)' : 'Message (AR)'} value={formData.messageAr ?? ''} onChange={(e) => updateForm('messageAr', e.target.value)} required />
              <Input label={isRtl ? 'نص الزر (EN)' : 'Button text (EN)'} value={formData.buttonTextEn ?? ''} onChange={(e) => updateForm('buttonTextEn', e.target.value)} required />
              <Input label={isRtl ? 'نص الزر (AR)' : 'Button text (AR)'} value={formData.buttonTextAr ?? ''} onChange={(e) => updateForm('buttonTextAr', e.target.value)} required />
              <Input label={isRtl ? 'رابط التوجيه (اختياري)' : 'Redirect URL (optional)'} value={formData.redirectUrl ?? ''} onChange={(e) => updateForm('redirectUrl', e.target.value)} />
              <Input label={isRtl ? 'تاريخ البداية' : 'Start date'} type="datetime-local" value={formData.startDate ?? ''} onChange={(e) => updateForm('startDate', e.target.value)} required />
              <Input label={isRtl ? 'تاريخ النهاية (اختياري)' : 'End date (optional)'} type="datetime-local" value={formData.endDate ?? ''} onChange={(e) => updateForm('endDate', e.target.value)} />
              <div className="form-group">
                <label className="form-label">
                  <input type="checkbox" checked={!!formData.isActive} onChange={(e) => updateForm('isActive', e.target.checked)} />
                  {isRtl ? 'نشط' : 'Active'}
                </label>
              </div>
              <Input label={isRtl ? 'الأولوية' : 'Priority'} type="number" value={formData.priority ?? 0} onChange={(e) => updateForm('priority', e.target.value)} min={0} />
              <Input label={isRtl ? 'ترتيب العرض' : 'Display order'} type="number" value={formData.displayOrder ?? 0} onChange={(e) => updateForm('displayOrder', e.target.value)} min={0} />
            </div>
          )}
        </form>
      </Modal>

      <style>{`
        .tabs-container {
          background: var(--surface);
          border-radius: var(--r-lg);
          overflow: hidden;
        }
        .tabs-header {
          display: flex;
          border-bottom: 1px solid var(--border);
          background: var(--neutral-50);
        }
        .tab-button {
          padding: var(--sp-4) var(--sp-6);
          background: transparent;
          border: none;
          border-bottom: 2px solid transparent;
          cursor: pointer;
          font-weight: 600;
          color: var(--neutral-600);
          transition: all var(--t-fast) var(--ease);
        }
        .tab-button:hover {
          color: var(--primary);
        }
        .tab-button.active {
          color: var(--primary);
          border-bottom-color: var(--primary);
        }
        .tab-content {
          padding: var(--sp-5);
        }
        .content-section {
          display: flex;
          flex-direction: column;
          gap: var(--sp-4);
        }
        .section-header {
          display: flex;
          justify-content: space-between;
          align-items: center;
        }
        .section-header h2 {
          font-size: var(--fs-xl);
          font-weight: 700;
          margin: 0;
        }
        .discount-form .form-fields {
          display: flex;
          flex-direction: column;
          gap: var(--sp-4);
          max-height: 60vh;
          overflow-y: auto;
        }
        .form-readonly {
          padding: var(--sp-3) var(--sp-4);
          font-weight: 600;
          color: var(--neutral-700);
          background: var(--neutral-100);
          border-radius: var(--r-md);
          font-size: var(--fs-sm);
        }
        .form-hint-error {
          color: var(--danger);
        }
        .product-typeahead-wrap {
          position: relative;
        }
        .product-typeahead {
          position: relative;
        }
        .product-typeahead-input {
          width: 100%;
        }
        .product-typeahead-selected {
          display: flex;
          align-items: center;
          justify-content: space-between;
          gap: var(--sp-2);
          padding: var(--sp-3) var(--sp-4);
          background: var(--neutral-50);
          border: 1px solid var(--border);
          border-radius: var(--r-md);
          min-height: 2.5rem;
        }
        .product-typeahead-selected-name {
          font-weight: 500;
          color: var(--neutral-800);
        }
        .product-typeahead-clear {
          padding: 0.25rem 0.5rem;
          font-size: var(--fs-xs);
          font-weight: 500;
          color: var(--primary);
          background: transparent;
          border: 1px solid var(--primary);
          border-radius: var(--r-sm);
          cursor: pointer;
          flex-shrink: 0;
        }
        .product-typeahead-clear:hover {
          background: rgba(30, 65, 35, 0.08);
        }
        .product-typeahead-dropdown {
          position: absolute;
          top: 100%;
          left: 0;
          right: 0;
          margin-top: 2px;
          max-height: 220px;
          overflow-y: auto;
          background: var(--surface);
          border: 1px solid var(--border);
          border-radius: var(--r-md);
          box-shadow: 0 8px 24px rgba(0,0,0,0.12);
          z-index: 100;
        }
        .product-typeahead-option {
          display: block;
          width: 100%;
          padding: var(--sp-3) var(--sp-4);
          text-align: inherit;
          font-size: var(--fs-sm);
          color: var(--neutral-800);
          background: none;
          border: none;
          cursor: pointer;
          transition: background 0.15s ease;
        }
        .product-typeahead-option:hover {
          background: var(--neutral-100);
        }
        .product-typeahead-empty {
          padding: var(--sp-4);
          font-size: var(--fs-sm);
          color: var(--neutral-500);
          text-align: center;
        }
        .modal-actions {
          display: flex;
          gap: var(--sp-3);
          justify-content: flex-end;
        }
        .table-actions {
          display: flex;
          gap: var(--sp-2);
          align-items: center;
          flex-wrap: wrap;
        }
        .btn-action {
          display: inline-flex;
          align-items: center;
          gap: 0.35rem;
          padding: 0.4rem 0.75rem;
          font-size: 0.8rem;
          font-weight: 500;
          border-radius: 6px;
          border: 1px solid transparent;
          cursor: pointer;
          transition: all 0.2s ease;
        }
        .btn-action.btn-edit {
          background: rgba(30, 65, 35, 0.08);
          color: var(--primary);
          border-color: rgba(30, 65, 35, 0.2);
        }
        .btn-action.btn-edit:hover {
          background: rgba(30, 65, 35, 0.12);
          border-color: var(--primary);
        }
        .btn-action.btn-delete {
          background: rgba(220, 53, 69, 0.08);
          color: var(--danger);
          border-color: rgba(220, 53, 69, 0.2);
        }
        .btn-action.btn-delete:hover {
          background: rgba(220, 53, 69, 0.14);
          border-color: var(--danger);
        }
      `}</style>
    </div>
  );
};

export default DiscountsIndex;
